import { useId, useRef, useState } from "react";

type FilePickerProps = {
  accept?: string;
  disabled?: boolean;
  label?: string;
  icon?: string;
  hint?: string;
  variant?: "dropzone" | "button";
  file?: File | null;
  busy?: boolean;
  onSelect: (file: File | null) => void;
};

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

export function FilePicker({
  accept,
  disabled,
  label = "Chọn tệp",
  icon = "upload_file",
  hint,
  variant = "dropzone",
  file,
  busy,
  onSelect,
}: FilePickerProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);
  const inputId = useId();

  const openPicker = () => {
    if (disabled || busy) return;
    inputRef.current?.click();
  };

  const acceptsFile = (f: File): boolean => {
    if (!accept) return true;
    const patterns = accept.split(",").map((p) => p.trim().toLowerCase());
    const type = (f.type || "").toLowerCase();
    const name = f.name.toLowerCase();
    return patterns.some((p) => {
      if (!p) return false;
      if (p.endsWith("/*")) return type.startsWith(p.slice(0, -1));
      if (p.startsWith(".")) return name.endsWith(p);
      return type === p;
    });
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    if (disabled || busy) return;
    const f = e.dataTransfer.files?.[0];
    if (f && acceptsFile(f)) onSelect(f);
  };

  const hiddenInput = (
    <input
      ref={inputRef}
      id={inputId}
      type="file"
      accept={accept}
      disabled={disabled || busy}
      style={{ display: "none" }}
      onChange={(e) => {
        const f = e.target.files?.[0] || null;
        onSelect(f);
        e.currentTarget.value = "";
      }}
    />
  );

  if (variant === "button") {
    return (
      <span className="file-picker file-picker--button">
        <button
          type="button"
          className="btn btn-secondary file-picker__trigger"
          disabled={disabled || busy}
          onClick={openPicker}
        >
          <span className="material-symbols-outlined" style={{ fontSize: 18 }}>
            {busy ? "progress_activity" : icon}
          </span>
          {busy ? "Đang tải…" : label}
        </button>
        {file && (
          <span className="file-picker__name" title={file.name}>
            <span className="file-picker__name-text">{file.name}</span>
            <span className="file-picker__name-size">{formatSize(file.size)}</span>
            {!busy && (
              <button
                type="button"
                className="file-picker__clear"
                aria-label="Bỏ tệp"
                onClick={() => onSelect(null)}
              >
                <span className="material-symbols-outlined" style={{ fontSize: 16 }}>close</span>
              </button>
            )}
          </span>
        )}
        {hiddenInput}
      </span>
    );
  }

  return (
    <div className="file-picker file-picker--dropzone">
      <button
        type="button"
        className={`file-picker__zone${dragOver ? " is-dragover" : ""}${disabled || busy ? " is-disabled" : ""}`}
        disabled={disabled || busy}
        onClick={openPicker}
        onDragOver={(e) => { e.preventDefault(); if (!disabled && !busy) setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
      >
        <span className="material-symbols-outlined file-picker__zone-icon">
          {busy ? "progress_activity" : icon}
        </span>
        <span className="file-picker__zone-label">{busy ? "Đang tải…" : label}</span>
        {hint && <span className="file-picker__zone-hint">{hint}</span>}
      </button>
      {file && (
        <div className="file-picker__name" title={file.name}>
          <span className="material-symbols-outlined" style={{ fontSize: 16 }}>description</span>
          <span className="file-picker__name-text">{file.name}</span>
          <span className="file-picker__name-size">{formatSize(file.size)}</span>
          {!busy && (
            <button
              type="button"
              className="file-picker__clear"
              aria-label="Bỏ tệp"
              onClick={() => onSelect(null)}
            >
              <span className="material-symbols-outlined" style={{ fontSize: 16 }}>close</span>
            </button>
          )}
        </div>
      )}
      {hiddenInput}
    </div>
  );
}
